import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

// Register required components
ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const TimeSeriesPlot = ({ timeSeriesData, objectId, title = "Time Series Data", loading = false }) => {
  const [chartData, setChartData] = useState(null);
  const [chartOptions, setChartOptions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return;
    }

    // Assume data is in format: [{time: timestamp, value: number, filter: string}, ...]
    // Group data by filter
    const filterGroups = {};
    
    timeSeriesData.forEach(point => {
      const filter = point.filter || 'default';
      if (!filterGroups[filter]) {
        filterGroups[filter] = [];
      }
      filterGroups[filter].push({
        x: new Date(point.time),
        y: point.value
      });
    });

    // Create datasets for each filter
    const datasets = Object.keys(filterGroups).map((filter, index) => {
      // Generate color based on index
      const hue = (index * 137) % 360; // Golden angle to get evenly distributed colors
      
      return {
        label: filter,
        data: filterGroups[filter],
        borderColor: `hsl(${hue}, 70%, 50%)`,
        backgroundColor: `hsla(${hue}, 70%, 50%, 0.2)`,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.3, // Slight curve for better visualization
      };
    });

    // Set chart data
    setChartData({
      datasets
    });

    // Set chart options
    setChartOptions({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'day',
            tooltipFormat: 'PP', // Localized date format
          },
          title: {
            display: true,
            text: 'Time',
          },
        },
        y: {
          title: {
            display: true,
            text: 'Value',
          },
        },
      },
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: title,
        },
        tooltip: {
          callbacks: {
            title: (tooltipItems) => {
              return new Date(tooltipItems[0].raw.x).toLocaleString();
            }
          }
        }
      },
    });
  }, [timeSeriesData, title]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-60">
        <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        <p>Error loading time series data: {error}</p>
      </div>
    );
  }

  if (!timeSeriesData || timeSeriesData.length === 0) {
    return (
      <div className="p-6 bg-white shadow-md rounded-lg">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
        <p className="text-gray-500 text-center py-4">
          No time series data available for this object
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white shadow-md rounded-lg">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
      <div className="h-80 w-full">
        {chartData && chartOptions && (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  );
};

export default TimeSeriesPlot;